import { useUpdate } from "@/core/hooks/use-update.hook";
import { DatabaseRecord } from "@/core/repos/table.repo";
import { TableColumnsType, ConfigProvider, Table, TablePaginationConfig, TableProps } from "antd";
import { createStyles } from "antd-style";
import { Dispatch, Key, ReactNode, SetStateAction, useState } from "react";
import { useDebounce } from 'use-debounce';
import { ColumnSearch, ColumnSearchPayload } from "./ColumnSearch";
import { normalizeVietnameseString } from "@/core/pipes/norm-vn.pipe";
import { WaterInvoiceBasic } from "@/core/repos/tables/water-invoice.repo";

const useStyle = createStyles(({ css }) => {
    return {
      customTable: css`
        .ant-table {
          .ant-table-container {
            .ant-table-body,
            .ant-table-content {
              scrollbar-width: thin;
              scrollbar-color: #eaeaea transparent;
              scrollbar-gutter: stable;
            }
          }
        }
      `,
    };
  });

export interface StaticDataTableProps<T extends DatabaseRecord> {
    columns: DataTableColumn<T>[];
    originalData: T[];
    actions?: ReactNode[];
    selectedRows: T[];
    setSelectedRows: Dispatch<SetStateAction<T[]>>;
    selectedRowKeys: Key[];
    setSelectedRowKeys: Dispatch<SetStateAction<Key[]>>;
    hideNo?: boolean;
    hideSelection?: boolean;
    summary?: () => ReactNode;
    loading?: boolean;
}

export interface DataTableColumn<T extends DatabaseRecord> {
    name: string,
    dataIndex?: string,
    render?: (text: any, record: T) => ReactNode,
    pipe?: (value: any) => string|ReactNode,
    raw?: (record: T) => string,
    fixed?: "right"|"left",
    width?: number,
    hideSearch?: boolean,
}

export function StaticDataTable<T extends DatabaseRecord>(props: StaticDataTableProps<T>) {
    const {
        columns,
        originalData,
        selectedRowKeys,
        setSelectedRowKeys,
        setSelectedRows,
        hideNo,
        hideSelection,
        summary,
        loading
    } = props;
    const tableColumns: TableColumnsType = [];
    const [searchMap, setSearchMap] = useState<Map<string, ColumnSearchPayload>>(new Map())
    const [debouncedSearchMap] = useDebounce(searchMap, 200);
    const [data, setData] = useState<T[]>(originalData);
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        current: 1,
        pageSize: 10,
        showSizeChanger: true
    })

    if (!hideNo) {
        tableColumns.push({
            title: <div className="p-2 text-center">STT</div>,
            key: 'key',
            dataIndex: 'key',
            fixed: 'left',
            width: 80,
            render: (_value: any, _record: T, index: number) => (<div className="px-4 py-3 h-full flex justify-center items-center"><p>{index + 1}</p></div>)
        });
    }

    function onSearch(event: ColumnSearchPayload) {
        console.log("Search event", event);
        const map = new Map(searchMap);
        map.set(event.dataIndex, event);
        setSearchMap(map);
    }

    const handleTableChange: TableProps<T>['onChange'] = (pagination) => {
        setPagination({
            ...pagination
        });
    };

    useUpdate(() => {
        if (debouncedSearchMap.size > 0) {
            const filteredData = originalData.filter((record: any) => {
                let match = true;
                debouncedSearchMap.forEach((payload, key) => {
                    const text = normalizeVietnameseString(record[key]?.toString() ?? "").toLowerCase();
                    const search = normalizeVietnameseString(payload.value).toLowerCase();
                    // Support different search modes
                    switch (payload.mode) {
                        case 'equals':
                            if (text !== search) match = false;
                            break;
                        case 'startsWith':
                            if (!text.startsWith(search)) match = false;
                            break;
                        case 'endsWith':
                            if (!text.endsWith(search)) match = false;
                            break;
                        case 'notInclude':
                            if (text.includes(search)) match = false;
                            break;
                        case 'include':
                        default:
                            if (!text.includes(search)) match = false;
                            break;
                    }
                    if (!match) return;
                });
                return match;
            });
            setData(filteredData);
        } else {
            setData(originalData);
        }
    }, [debouncedSearchMap, originalData]);

    function buildColumn(column: DataTableColumn<T>) {
        return {
            title: <div className="px-4 py-6">{column.name}</div>,
            key: column.dataIndex,
            dataIndex: column.dataIndex,
            fixed: column.fixed,
            width: column.width,
            children: [
                {
                    title: (column.dataIndex&&!column.hideSearch)
                        ? <div className="h-8 table-row-dark bg-white">
                            <ColumnSearch onChange={onSearch} dataIndex={column.dataIndex}/>
                        </div>
                        : <div className="h-8 table-row-dark"></div>,
                    width: column.width,
                    dataIndex: column.dataIndex,
                    render: column.render ? column.render : (value: string) => <div className="px-4 flex flex-col justify-center h-[60px]">{column.pipe ? column.pipe(value) : value}</div>,
                }
            ],
        }
    }

    columns.forEach(column => {
        tableColumns.push(buildColumn(column));
    });
    const { styles } = useStyle();
    return (
        <ConfigProvider theme={{
            components: {
                Table: {
                    headerBg: "var(--primary-color)",
                    headerSplitColor: "white",
                    headerColor: "white",
                    cellPaddingBlock: 0,
                    cellPaddingInline: 0,
                    headerBorderRadius: 0,
                    cellFontSize: 14,
                    fontWeightStrong: 600,
                    lineWidth: 1.5,
                },
                Input: {
                    inputFontSize: 14,
                    paddingBlock: 6
                }
            },
            token: {
                colorPrimary: "#0B7DB6",
            }
        }}>
            <Table
                rowHoverable={false}
                dataSource={data}
                rowKey={(record: T) => record.id}
                columns={tableColumns} bordered={true}
                rowClassName={(_, index) => index % 2 === 0 ? 'table-row-light': 'table-row-dark'}
                rowSelection={hideSelection ? undefined : {
                    type: 'checkbox',
                    columnWidth: 60,
                    renderCell: (_, __, ___, node) => (<div className="flex h-full justify-center items-center">{node}</div>),
                    selectedRowKeys: selectedRowKeys,
                    preserveSelectedRowKeys: true,
                    onChange(selectedRowKeys, selectedRows) {
                        setSelectedRows(selectedRows);
                        setSelectedRowKeys(selectedRowKeys)
                    },
                }}
                onChange={handleTableChange}
                pagination={false}
                className={styles.customTable}
                summary={summary}
                virtual
                scroll={{ y: "60vh" }}
                loading={loading}
            />
        </ConfigProvider>
    );
}
